/**
 * RodeoMasterPrizeToggles — rodeo-level master switches for the three per-event
 * prize-display toggles, shown in the My Rodeos rodeo header (collapsed or not).
 *
 * Each master switch shows an "on / total" count (e.g. 8/13 — how many of the
 * rodeo's events currently have that toggle on) and, when flipped, applies that
 * same toggle ON or OFF to every event in the rodeo at once. It mirrors the
 * compact per-event switches in `EventPrizeDisplayToggles` (same one-word
 * labels) so the two surfaces read as one feature.
 *
 * Effective-state semantics (kept identical to the per-event component):
 *   - Contestants → display_prize_to_contestants
 *   - Results     → show_prize_amounts_in_results
 *   - Live        → show_prize_amounts_in_results AND
 *                   show_prize_amounts_before_complete (Live only counts where
 *                   Results is also on, matching the per-event dependency rule).
 *
 * A master switch reads ON only when every event has that toggle on; a partial
 * state (count < total) shows OFF, and the count communicates the spread.
 * Flipping persistence lives in the parent (this component is presentational).
 */

import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PRIZE_TOGGLE_LABELS } from '@/components/delegate/EventPrizeDisplayToggles';

/** Which of the three toggles a master switch drives. */
export type PrizeToggleField = 'display' | 'results' | 'live';

export interface RodeoMasterPrizeTogglesProps {
  /** All events in the rodeo (raw rows from the events list). */
  events: any[];
  /** Apply `target` to `field` across every event in the rodeo. */
  onApply: (field: PrizeToggleField, target: boolean) => void;
  /** Disable while a bulk save is in flight. */
  disabled?: boolean;
  /** Unique prefix so switch ids stay distinct across rodeo rows. */
  idPrefix: string;
}

/** Effective per-event on-state for each toggle field. */
function isFieldOn(event: any, field: PrizeToggleField): boolean {
  const results = (event as any)?.show_prize_amounts_in_results !== false;
  switch (field) {
    case 'display':
      return !!(event as any)?.display_prize_to_contestants;
    case 'results':
      return results;
    case 'live':
      return results && !!(event as any)?.show_prize_amounts_before_complete;
  }
}

interface MasterToggleProps {
  id: string;
  label: string;
  title: string;
  onCount: number;
  total: number;
  disabled?: boolean;
  onCheckedChange: (v: boolean) => void;
}

function MasterToggle({
  id,
  label,
  title,
  onCount,
  total,
  disabled,
  onCheckedChange,
}: MasterToggleProps) {
  const allOn = total > 0 && onCount === total;
  return (
    <div
      className={`flex flex-col items-center gap-0.5 ${disabled ? 'opacity-50' : ''}`}
      title={title}
    >
      <Label
        htmlFor={id}
        className="text-[9px] font-medium uppercase tracking-wide text-muted-foreground leading-none cursor-pointer"
      >
        {label}
      </Label>
      <span className="text-[10px] tabular-nums leading-none text-muted-foreground">
        {onCount}/{total}
      </span>
      <Switch
        id={id}
        checked={allOn}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        className="scale-[0.7]"
      />
    </div>
  );
}

export function RodeoMasterPrizeToggles({
  events,
  onApply,
  disabled,
  idPrefix,
}: RodeoMasterPrizeTogglesProps) {
  const total = events.length;
  if (total === 0) return null;

  const count = (field: PrizeToggleField) =>
    events.reduce((n, ev) => n + (isFieldOn(ev, field) ? 1 : 0), 0);

  return (
    <div className="flex items-center gap-2.5">
      <MasterToggle
        id={`${idPrefix}-master-display`}
        label={PRIZE_TOGGLE_LABELS.display_prize_to_contestants}
        title="Show prize to contestants for every event in this rodeo at once."
        onCount={count('display')}
        total={total}
        disabled={disabled}
        onCheckedChange={(v) => onApply('display', v)}
      />
      <MasterToggle
        id={`${idPrefix}-master-results`}
        label={PRIZE_TOGGLE_LABELS.show_prize_amounts_in_results}
        title="Show prize amounts in results for every event in this rodeo at once."
        onCount={count('results')}
        total={total}
        disabled={disabled}
        onCheckedChange={(v) => onApply('results', v)}
      />
      <MasterToggle
        id={`${idPrefix}-master-live`}
        label={PRIZE_TOGGLE_LABELS.show_prize_amounts_before_complete}
        title="Show prize amounts before all contestants compete for every event at once. Only takes effect on events that also have Results on."
        onCount={count('live')}
        total={total}
        disabled={disabled}
        onCheckedChange={(v) => onApply('live', v)}
      />
    </div>
  );
}

export default RodeoMasterPrizeToggles;
